import React, { useState, useEffect } from 'react';

import Dialog from 'components/Dialog';
import { ElevationProps } from 'components/Elevation';
import { zIndex } from 'styles';

import DialogSheetFadeAnimation from './DialogSheetFadeAnimation';

interface DialogSheetContextInterface {
  updateDialogOpen?: (any) => void;
  updateIsOpen?: (any) => void;
}

const DialogSheetContext = React.createContext<DialogSheetContextInterface | null>({});

const OVERLAY_STYLE = { background: 'transparent', zIndex: zIndex.DIALOG };
const CONTENT_STYLE:React.CSSProperties = {
  position: 'absolute',
  top: 'auto',
  left: 'auto',
  right: 'auto',
  bottom: 'auto',
  border: 0,
  background: 'transparent',
  backgroundColor: 'none',
  borderRadius: 0,
  outline: 'none',
  padding: 0,
  height: '100%',
  width: '100%',
  overflow: 'hidden'
};

export interface DialogSheetProps extends Omit<React.ComponentPropsWithRef<'div'>, 'style'> {
  /** The content of the DialogSheet, consider using `DialogSheetContentContainer`,`DialogSheetTitle`,`DialogContent` and `DialogFooter` */
  children: React.ReactNode;
  /** Set `true`, to render the dialog sheet. */
  isOpen?: boolean;
  /** The container's <Elevation /> configuration, see the `<Elevation/>` documentation for more. */
  elevationProps?: Partial<ElevationProps>;
  /** Controls animation(Fade) for DialogSheet(Overlay). See react-spring transition config for more information (https://react-spring.io). */
  animationProps?: any;
  /** If true, disable all motion animation for this component.  This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
}

/** DialogSheet, All other props are provided to the Dialog (see documentation) which is developed using http://reactcommunity.org/react-modal/ library. */
const DialogSheet = (props: DialogSheetProps) => {
  const {
    children,
    isOpen,
    elevationProps,
    animationProps,
    disableAnimation,
    ...otherProps
  } = props;

  const [showContent, setShowContent] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    isOpen && setShowContent(true);
    isOpen && setOpenDialog(true);
  }, [isOpen]);

  return (
    <DialogSheetContext.Provider
      value={{
        updateIsOpen: value => setShowContent(value),
        updateDialogOpen: value => setOpenDialog(value)
      }}
    >
      <Dialog
        isOpen={openDialog}
        style={{ overlay: OVERLAY_STYLE, content: CONTENT_STYLE }}
        elevationProps={elevationProps}
        {...otherProps}
      >
        <DialogSheetFadeAnimation
          isOpen={isOpen}
          showContent={showContent}
          disableAnimation={disableAnimation}
          {...animationProps}
        >
          {children}
        </DialogSheetFadeAnimation>
      </Dialog>
    </DialogSheetContext.Provider>
  );
};

DialogSheet.defaultProps = {
  isOpen: false,
  elevationProps: {
    elevationIndex: 0
  },
  animationProps: {},
  disableAnimation: undefined,
};

export {
  DialogSheet as default,
  DialogSheetContext
};